import Box from "@amzn/meridian/box";
import { BoxProps } from "@amzn/meridian/box/box";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import Checkbox from "@amzn/meridian/checkbox";
import Column from "@amzn/meridian/column";
import { ColumnProps } from "@amzn/meridian/column/column";
import ControlGroup from "@amzn/meridian/control-group";
import DatePicker from "@amzn/meridian/date-picker";
import Input from "@amzn/meridian/input";
import { InputProps } from "@amzn/meridian/input/input";
import Row from "@amzn/meridian/row";
import { RowProps } from "@amzn/meridian/row/row";
import Select from "@amzn/meridian/select";
import SelectOption, { SelectOptionProps } from "@amzn/meridian/select/select-option";
import Text from "@amzn/meridian/text";
import Textarea from "@amzn/meridian/textarea";
import _ from "lodash";
import escapeRegExp from "lodash/escapeRegExp";
import React, { memo, useState } from "react";
import { SheetFormInput } from "./SheetFormInput";
import SiteImprovementDetails from "./siteImprovementDetails";
import { useLocation } from "react-router-dom";

export interface IWrapperInputProps extends InputProps {
    inputKey: string;
    inputType: string;
    fieldName: string;
    fieldProps?: any;
    boxProps?: BoxProps;
    inputProps?: any;
    columnProps?: ColumnProps;
    rowProps?: RowProps;
    inputHelperButtons?: Array<any>;
    selectOptions?: Array<SelectOptionProps>;
    checkboxOptions?: Array<any>;
    groupButtonOptions?: Array<any>;
    inputOptions: Array<any>;
    sheetName?: string;
}

export const InputWrapper: React.FC<IWrapperInputProps> = memo(
    ({
        fieldName,
        inputKey,
        inputType,
        fieldProps,
        boxProps,
        inputProps,
        columnProps,
        rowProps,
        selectOptions,
        checkboxOptions,
        groupButtonOptions,
        sheetName,
    }) => {
        const { field, form } = fieldProps;
        const { setFieldValue, handleBlur, errors, submitCount, getFieldMeta } = form;
        const { error, touched } = getFieldMeta(field.name);
        const { pathname } = useLocation();

        const [searchQuery, setSearchQuery] = useState();
        const searchRegExp = new RegExp(escapeRegExp(searchQuery), "i");
        const matchedOptions = selectOptions?.filter(
            option => !searchQuery || searchRegExp.test(option.label.toString())
        );

        const inputHideConditionHandler = inputProps => {
            return (
                "hideInputOnCondition" in inputProps &&
                inputProps.hideInputOnCondition.isHideConditionTrue(form)
            );
        };

        const getInput = inputType => {
            switch (inputType) {
                case "standardInput": {
                    return (
                        <Input
                            key={`${inputKey}_input`}
                            {...inputProps}
                            value={field.value || null}
                            onChange={newValue => {
                                form.setFieldValue(
                                    field.name,
                                    inputProps.inputMode === "numeric"
                                        ? parseFloat(newValue)
                                        : newValue
                                );
                            }}
                            disabled={inputProps?.disableInputOnCondition?.condition(pathname)}
                            placeholder={
                                inputProps?.getConditionalPlaceHolder?.condition(form) ||
                                inputProps?.placeholder
                            }
                            error={error}
                            errorMessage={touched && error}
                            onBlur={() => form.handleBlur(field.name)}
                            type={inputProps.inputMode === "numeric" ? "Number" : "Text"}
                        />
                    );
                }
                case "select": {
                    return (
                        <Select
                            key={`${inputKey}_select`}
                            {...inputProps}
                            value={field.value || null}
                            popoverMaxHeight={200}
                            searchQuery={searchQuery}
                            onSearch={setSearchQuery}
                            onChange={newValue => {
                                setFieldValue(fieldName, newValue);
                            }}
                            error={error}
                            onBlur={handleBlur}
                            errorMessage={error}
                        >
                            {matchedOptions.map(option => {
                                return <SelectOption value={option.value} label={option.label} />;
                            })}
                            {!matchedOptions.length ? (
                                <Column alignmentVertical="center" spacing="300" spacingInset="600">
                                    <Text alignment="center">No results</Text>
                                </Column>
                            ) : null}
                        </Select>
                    );
                }
                case "datePicker": {
                    return (
                        <DatePicker
                            key={`${inputKey}_datepicker`}
                            {...inputProps}
                            value={field.value || "null"}
                            onChange={newValue => {
                                setFieldValue(fieldName, newValue);
                            }}
                            error={error}
                            onBlur={handleBlur}
                            errorMessage={touched && error}
                        />
                    );
                }
                case "textArea": {
                    return (
                        <Textarea
                            key={`${inputKey}_textarea`}
                            {...inputProps}
                            value={field.value || ""}
                            onChange={newValue => {
                                setFieldValue(fieldName, newValue);
                            }}
                            onBlur={handleBlur}
                            errorMessage={touched && error}
                        />
                    );
                }
                case "groupCheckbox": {
                    return (
                        <React.Fragment>
                            <Text color={"error"}>
                                {errors ? getFieldMeta(fieldName).error : null}
                            </Text>
                            <ControlGroup
                                key={`${inputKey}_textarea`}
                                {...inputProps}
                                value={field.value || null}
                                onChange={newValue => {
                                    setFieldValue(fieldName, newValue);
                                }}
                                onBlur={handleBlur}
                                errorMessage={touched && error}
                            >
                                {checkboxOptions.map(checkbox => {
                                    return (
                                        <Checkbox value={checkbox.value}>{checkbox.label}</Checkbox>
                                    );
                                })}
                            </ControlGroup>
                        </React.Fragment>
                    );
                }
                case "groupButton": {
                    return (
                        <>
                            <Text>{inputProps.label} </Text>
                            <ButtonGroup
                                key={`${inputKey}_textarea`}
                                {...inputProps}
                                value={field.value || null}
                                onChange={newValue => {
                                    setFieldValue(fieldName, newValue);
                                }}
                                onBlur={handleBlur}
                                errorMessage={submitCount > 0 && _.get(errors, fieldName)}
                            >
                                {groupButtonOptions.map(option => {
                                    return (
                                        <ButtonOption value={option.value}>
                                            {option.label}
                                        </ButtonOption>
                                    );
                                })}
                            </ButtonGroup>
                        </>
                    );
                }
                case "sheet": {
                    "inputOnCondition" in inputProps &&
                        inputProps.inputOnCondition.setPrimarySpace(form);

                    return (
                        <SheetFormInput
                            inputProps={inputProps}
                            field={field}
                            sheetName={sheetName}
                            form={form}
                        />
                    );
                }
                case "SiteImprovementDetails": {
                    return (
                        <SiteImprovementDetails
                            key={`${inputKey}_other_improvements_input`}
                            fieldProps={fieldProps}
                        />
                    );
                }
            }
        };

        return (
            <Row key={`${inputKey}_row`} {...rowProps}>
                {inputHideConditionHandler(inputProps) ? null : (
                    <Column {...columnProps}>
                        <Box key={`${inputKey}_input_box`} {...boxProps}>
                            {getInput(inputType)}
                        </Box>
                    </Column>
                )}
            </Row>
        );
    }
);
